import { createContext, ReactNode, useState } from 'react';

export interface Product {
  id: number;
  name: string;
  price: number;
  color: 'red' | 'blue' | 'green';
}

export interface CartItem extends Product {
  quantity: number;
}

interface CheckoutProviderProps {
  children: ReactNode;
}

interface CheckoutContextType {
  cartItems: CartItem[];
  addToCart: (itemToAdd: Product | CartItem) => void;
  removeFromCart: (itemToRemove: CartItem) => void;
  decrementCartItem: (itemToRemove: CartItem) => void;
}

const addCartItem = (cartItems: CartItem[], itemToAdd: Product) => {
  const existing = cartItems.find((item) => item.id === itemToAdd.id);
  if (existing) {
    return cartItems.map((item) =>
      item.id === existing.id ? { ...item, quantity: item.quantity + 1 } : item,
    );
  }
  return [...cartItems, { ...itemToAdd, quantity: 1 }];
};

const removeCartItem = (
  cartItems: CartItem[],
  itemToRemove: CartItem,
  removeAll: boolean = false,
) => {
  const existing = cartItems.find((item) => item.id === itemToRemove.id);
  if (!existing) return cartItems;
  return removeAll || existing.quantity === 1
    ? cartItems.toSpliced(
        cartItems.findIndex((item) => item.id === existing.id),
        1,
      )
    : cartItems.map((item) =>
        item.id === existing.id
          ? { ...item, quantity: item.quantity - 1 }
          : item,
      );
};

export const CheckoutContext = createContext<CheckoutContextType>({
  cartItems: [],
  addToCart: () => {},
  removeFromCart: () => {},
  decrementCartItem: () => {},
});

export const CheckoutProvider = ({ children }: CheckoutProviderProps) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  const addToCart = (itemToAdd: Product | CartItem) => {
    setCartItems(addCartItem(cartItems, itemToAdd));
  };

  const removeFromCart = (itemToRemove: CartItem) => {
    setCartItems(removeCartItem(cartItems, itemToRemove, true));
  };

  const decrementCartItem = (itemToRemove: CartItem) => {
    setCartItems(removeCartItem(cartItems, itemToRemove));
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    decrementCartItem,
  };

  return <CheckoutContext value={value}>{children}</CheckoutContext>;
};
